import { Link, useParams } from "react-router-dom";
import { Page } from "../../../common/components/Page";
import { MaterialWaiverGate } from "../../../common/components/payment";
import { useWaiverGate } from "@bw/shared/hooks";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * Player-owned facility waiver action reached from a staff-initiated drop-in.
 * It deliberately contains no payment or check-in mutation: staff retains the
 * original customer/payment attempt and the player only supplies their own
 * non-delegable legal acceptance.
 */
export function DropInWaiverPage() {
  const { facilityId = "" } = useParams<{ facilityId: string }>();
  const waiver = useWaiverGate(facilityId);

  const facilityName = waiver.currentWaiver?.facility_name.trim();

  return (
    <Page>
      <Stack spacing={0.5}>
        <Typography variant="overline" color="primary.main">
          Drop-in waiver
        </Typography>
        <Typography component="h1" variant="h3">
          {facilityName || "Facility waiver"}
        </Typography>
        <Typography color="text.secondary">
          Facility staff started a drop-in check-in for you. Accept your own
          waiver here, then return to staff to finish check-in.
        </Typography>
      </Stack>

      {waiver.statusError ? (
        <Alert severity="error" role="alert">
          <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
            <span>We couldn&apos;t confirm the current waiver.</span>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="small"
              onClick={waiver.retryStatus}
            >
              Retry
            </Button>
          </Stack>
        </Alert>
      ) : waiver.isLoading ? (
        <Stack
          direction="row"
          spacing={1}
          role="status"
          sx={{ alignItems: "center" }}
        >
          <CircularProgress size={20} />
          <Typography color="text.secondary">
            Loading the current waiver…
          </Typography>
        </Stack>
      ) : waiver.currentVersionId === null ? (
        <Stack spacing={1.5} role="status" sx={{ alignItems: "flex-start" }}>
          <Typography>
            No waiver is currently required at this facility.
          </Typography>
          <Button
            component={Link}
            variant="outlined"
            to={`/dropin/${facilityId}`}
          >
            View drop-in
          </Button>
        </Stack>
      ) : waiver.isBlocked ? (
        <MaterialWaiverGate
          currentWaiver={waiver.currentWaiver}
          accepting={waiver.isAccepting}
          acceptError={waiver.acceptError}
          onAccept={waiver.accept}
        />
      ) : (
        <Stack spacing={1.5}>
          <MaterialWaiverGate
            accepted
            currentWaiver={waiver.currentWaiver}
            accepting={waiver.isAccepting}
            acceptError={waiver.acceptError}
            onAccept={waiver.accept}
          />
          <Alert severity="success" role="status">
            Return to facility staff to finish check-in.
          </Alert>
        </Stack>
      )}
    </Page>
  );
}
